import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check } from 'lucide-react';
import type { MotivationStyle } from '@/contexts/GamificationContext';
import { useLanguage } from '@/contexts/LanguageContext';

interface StepMotivationProps {
  onNext: (style: MotivationStyle) => void;
  onSkip: () => void;
}

export function StepMotivation({ onNext, onSkip }: StepMotivationProps) {
  const { t } = useLanguage();
  const [selected, setSelected] = useState<MotivationStyle>('gentle');

  const options = [
    {
      style: 'minimal' as MotivationStyle,
      title: t('onboarding.motivation.minimal'),
      preview: t('onboarding.motivation.minimalPreview'),
      description: t('onboarding.motivation.minimalDesc'),
    },
    {
      style: 'gentle' as MotivationStyle,
      title: t('onboarding.motivation.gentle'),
      preview: t('onboarding.motivation.gentlePreview'),
      description: t('onboarding.motivation.gentleDesc'),
      recommended: true,
    },
    {
      style: 'gamified' as MotivationStyle,
      title: t('onboarding.motivation.gamified'),
      preview: t('onboarding.motivation.gamifiedPreview'),
      description: t('onboarding.motivation.gamifiedDesc'),
    },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-foreground">
          {t('onboarding.motivation.title')}
        </h2>
        <p className="text-muted-foreground">
          {t('onboarding.motivation.subtitle')}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('onboarding.changeLater')}
        </p>
      </div>

      <div className="space-y-3">
        {options.map((option) => (
          <Card
            key={option.style}
            className={`p-4 cursor-pointer transition-all ${
              selected === option.style
                ? 'border-primary bg-primary/5 shadow-lg'
                : 'border-border hover:border-primary/50'
            }`}
            onClick={() => setSelected(option.style)}
          >
            <div className="flex items-start justify-between">
              <div className="space-y-1 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-foreground">
                    {option.title}
                  </h3>
                  {option.recommended && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      {t('onboarding.motivation.recommended')}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {option.description}
                </p>
                <div className="mt-2 p-2 bg-muted/50 rounded border border-muted">
                  <code className="text-sm text-foreground">{option.preview}</code>
                </div>
              </div>
              {selected === option.style && (
                <Check className="w-5 h-5 text-primary flex-shrink-0 ml-2" />
              )}
            </div>
          </Card>
        ))}
      </div>

      <div className="space-y-2 pt-2">
        <Button
          onClick={() => onNext(selected)}
          className="w-full min-h-touch"
        >
          {t('onboarding.motivation.continue')}
        </Button>
        <button 
          onClick={onSkip}
          className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
        >
          {t('onboarding.motivation.skip')}
        </button>
      </div>
    </div>
  );
}
